/**
 * The partner ledger's shared components (TMMPDM-0095, TMMAPI-0162 U3/U4): a statement's status chip,
 * a hold's kind chip and its words, the frozen-history banner on today's payout screens, and the 410 message an old
 * payout action says in its dialog. The logic they use is in `ledger.ts`.
 */
import { Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import { StatusChip } from '@/status';
import {
  LEDGER_HOLD_KINDS,
  type LedgerHoldView,
  type LedgerStatementStatus,
} from '@/api/tmm/types';
import type { Translate } from './ledger';

const STATUS_FALLBACK: Record<LedgerStatementStatus, string> = {
  CARRIED: 'Carried',
  HELD: 'Held',
  NEGATIVE: 'Negative',
  PAYABLE: 'Payable',
  PAYOUT_FAILED: 'Payout failed',
  COLLECTING: 'Collecting',
  PAYING: 'Paying',
  PAID: 'Paid',
  PAID_EXTERNALLY: 'Paid externally',
  COLLECTED: 'Collected',
  SETTLED: 'Settled',
};

/** A statement's status, as sent, in words and its registered tone. */
export function LedgerStatusChip({ status }: { status: string }) {
  const { t } = useCopy();
  const fallback = Object.hasOwn(STATUS_FALLBACK, status)
    ? STATUS_FALLBACK[status as LedgerStatementStatus]
    : status;
  return <StatusChip status={status} label={t(`ledger.statementStatus.${status}`, fallback)} />;
}

const HOLD_FALLBACK: Record<string, string> = {
  FOR_CAUSE: 'For cause',
  REVIEW: 'Review',
  TAX_ID_UNVERIFIED: 'Tax ID unverified',
  PAYOUT_RETURNED: 'Payout returned',
  COLLECTION_CONFLICT: 'Collection conflict',
  OTHER: 'Other: {{kind}}',
};

type HoldKindOf = Pick<LedgerHoldView, 'kind' | 'rawKind'>;

/**
 * The hold as the console reads it: a kind the contract names as itself; any other kind (a value tmm-api
 * sends that this console doesn't know yet) as `OTHER` with that kind as the raw one. A blank kind is
 * null — that is not `OTHER`. `kind` is required.
 */
function knownHold(hold: HoldKindOf): { kind: string; rawKind?: string } | null {
  if (hold.kind === '') return null;
  return (LEDGER_HOLD_KINDS as readonly string[]).includes(hold.kind)
    ? { kind: hold.kind, ...(hold.rawKind !== undefined ? { rawKind: hold.rawKind } : {}) }
    : { kind: 'OTHER', rawKind: hold.kind };
}

/** A hold's kind in words; `OTHER` names the raw kind pay-api sent. "—" when the kind is blank. */
function holdKindLabel(hold: HoldKindOf, t: Translate): string {
  const known = knownHold(hold);
  if (known === null) return '—';
  if (known.kind === 'OTHER') {
    return t('ledger.holdKind.OTHER', HOLD_FALLBACK.OTHER, { kind: known.rawKind ?? '—' });
  }
  return t(`ledger.holdKind.${known.kind}`, HOLD_FALLBACK[known.kind] ?? known.kind);
}

/** A hold's kind as a chip, in its registered tone (a kind the console doesn't know: `OTHER`'s). */
export function HoldKindChip({ hold }: { hold: HoldKindOf }) {
  const { t } = useCopy();
  const known = knownHold(hold);
  if (known === null) return <>—</>;
  return <StatusChip status={known.kind} domain="ledgerHold" label={holdKindLabel(hold, t)} />;
}

/** A hold's kind in words, as text (a dialog's title names the hold it acts on). */
export function HoldKindName({ hold }: { hold: HoldKindOf }) {
  const { t } = useCopy();
  return <>{holdKindLabel(hold, t)}</>;
}

/**
 * One ledger amount. No currency (absent or empty) is "—": the console does not invent one.
 * A missing minor is "—" inside `Amount`, and a sent 0 stays 0.
 */
export function LedgerMoney({
  minor,
  currency,
  emphasis = false,
}: {
  minor?: number | null | undefined;
  currency?: string | null | undefined;
  emphasis?: boolean;
}) {
  if (typeof currency !== 'string' || currency === '') return <>{'—'}</>;
  return (
    <Amount
      minor={minor}
      currency={currency}
      accounting
      {...(emphasis ? { emphasis: true } : {})}
    />
  );
}

/**
 * The banner on today's payout screens once the ledger is `LIVE` (Step 4): what is shown is history.
 * `ledgerTo` is the partner's Ledger leaf where the screen is one partner's; the fleet run sheet has none.
 */
export function FrozenHistoryBanner({ ledgerTo }: { ledgerTo?: string | undefined }) {
  const { t } = useCopy();
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      {t('ledger.frozen.banner', 'This is history from before the partner ledger.')}{' '}
      {ledgerTo !== undefined ? (
        <Link component={RouterLink} to={ledgerTo} underline="always">
          {t('ledger.frozen.link', "Use the partner's Ledger.")}
        </Link>
      ) : (
        t('ledger.frozen.link', "Use the partner's Ledger.")
      )}
    </Alert>
  );
}

/**
 * Step 5: an old action that answered 410 `PAYOUTS_MOVED_TO_LEDGER`, said inside its dialog with the
 * link to the partner's Ledger (when the dialog knows the partner).
 */
export function MovedToLedgerMessage({ ledgerTo }: { ledgerTo?: string | undefined }) {
  const { t } = useCopy();
  return (
    <>
      {t(
        'errors.code.PAYOUTS_MOVED_TO_LEDGER',
        "Payouts have moved to the partner ledger. Use the partner's Ledger instead.",
      )}
      {ledgerTo !== undefined ? (
        <>
          {' '}
          <Link component={RouterLink} to={ledgerTo} underline="always">
            {t('ledger.frozen.open', 'Open the Ledger')}
          </Link>
        </>
      ) : null}
    </>
  );
}
